import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  setCursorTooltip,
  updateTool,
} from '../../../../redux/actions/canvas_editor';
import Button from '../Toolbar/Button';
import { connect } from 'react-redux';
import ThinIcon from '../../images/Icon20_Pencil.svg';

import { getThicknessPx, getDrawingBounds } from '../../helpers/fabricHelpers';

import { __ } from '../../../../core/utils';

class ClippedBrush extends fabric.PencilBrush {
  constructor(canvas, width, height) {
    super(canvas);
    this.source = { width, height };
  }

  _addPoint(point) {
    if (!point) return;

    const x = Math.max(0, Math.min(this.source.width, point.x));
    const y = Math.max(0, Math.min(this.source.height, point.y));

    super._addPoint(new fabric.Point(x, y));
    this.canvas.requestRenderAll();
  }
}

function Pencil({
  canvas_editor: { viewer, color, selectedThickness, fabricCanvases },
  source: { width, height },
  isActive,
  sourceType,
  user_id,
  user_full_name,
}) {
  const handleClick = () => {
    if (!isActive) updateTool({ tool: 'pencil' });
  };

  useEffect(() => {
    if (!isActive || !fabricCanvases) return;

    setCursorTooltip(__('canvas.tooltip.free_draw'));

    const disposers = [];

    Object.entries(fabricCanvases).forEach(([pageNumber, canvas]) => {
      canvas.selection = false;
      canvas.defaultCursor = 'crosshair';
      canvas.isDrawingMode = true;

      if (sourceType == 'pdf') {
        const originalGetPointer = canvas.getPointer.bind(canvas);
        canvas.getPointer = (e, ignoreZoom) => {
          const pointer = originalGetPointer(e, ignoreZoom);
          const bounds = getDrawingBounds(canvas, { width, height }, sourceType);
          return {
            x: Math.max(0, Math.min(bounds.width, pointer.x)),
            y: Math.max(0, Math.min(bounds.height, pointer.y)),
          };
        };
      } else {
        canvas.freeDrawingBrush = new ClippedBrush(canvas, width, height);
      }
      const pdfScaleFactor =
        sourceType === 'pdf' ? canvas?.pdfScaleFactor || 1 : 1;
      canvas.freeDrawingBrush.color = color.hex;
      canvas.freeDrawingBrush.width =
        getThicknessPx(selectedThickness.key, 'pencil') * pdfScaleFactor;

      const onPointerDown = () => {
        canvas.isDrawingMode = true;
        const pointerPdfScaleFactor =
          sourceType === 'pdf' ? canvas?.pdfScaleFactor || 1 : 1;
        canvas.freeDrawingBrush.width =
          getThicknessPx(selectedThickness.key, 'pencil') *
          pointerPdfScaleFactor;
        canvas.freeDrawingBrush.color = color.hex;

        viewer?.setMouseNavEnabled(false);
      };
      const onPathCreated = (e) => {
        const obj = e.path;
        if (!obj) return;

        obj.objectType = 'freehand';
        obj.userID = user_id;
        obj.userFullName = user_full_name;
        obj.lockScalingX = true;
        obj.lockScalingY = true;
        obj.hasControls = false;
        obj.hasBorders = false;
        obj.perPixelTargetFind = true;
        obj.strokeUniform = true;
        obj.pageNumber = pageNumber;

        obj.lastGoodScaleX = obj.scaleX;
        obj.lastGoodScaleY = obj.scaleY;
        obj.lastGoodLeft = obj.left;
        obj.lastGoodTop = obj.top;

        obj.setCoords();
        viewer?.setMouseNavEnabled(true);
      };
      const onMouseUp = () => viewer?.setMouseNavEnabled(true);

      canvas.upperCanvasEl.addEventListener('pointerdown', onPointerDown);
      canvas.on('path:created', onPathCreated);
      canvas.on('mouse:up', onMouseUp);

      disposers.push(() => {
        if (canvas?.upperCanvasEl)
          canvas.upperCanvasEl.removeEventListener(
            'pointerdown',
            onPointerDown
          );

        canvas.isDrawingMode = false;

        canvas.off('path:created', onPathCreated);
        canvas.off('mouse:up', onMouseUp);
      });
    });

    return () => disposers.forEach((dispose) => dispose());
  }, [
    isActive,
    user_id,
    viewer,
    color,
    selectedThickness,
    fabricCanvases,
    width,
    height,
    sourceType,
  ]);

  return (
    <Button
      id='pencil'
      label={__('canvas.toolbar.pencil')}
      icon={ThinIcon}
      active={isActive}
      onClick={handleClick}
    />
  );
}

Pencil.propTypes = {
  isActive: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  canvas_editor: state.canvas_editor,
  user_id: state.auth.id,
  user_full_name: state.auth.firstname + ' ' + state.auth.lastname,
});

export default connect(mapStateToProps, null)(Pencil);
